import * as React from "react";
import * as moment from "moment";
import { map as _map, isEmpty as _isEmpty, flatten as _flatten, isEmpty, forEach as _forEach } from "lodash";
import LineChart from "../components/common/LineChart";
import { ICgmStats, IDailySugarStatValue, IPatientActivityImpactItem, IPatientActivityLoggingResponse, IRangeBucket, IUserMetricValue } from "../models/ICgmStats";
import ActivitiesTable, { IActivitiesTableProps, IActivity } from "../components/AGM/ActivitiesTable";

interface IPatientData {
    dateEpoch: number,
    glucoseReadings?: any[],
    activities: any[]
}

interface IGlucoseProfilesProps {
    cgmStats: ICgmStats
}

const GlucoseProfiles: React.SFC<IGlucoseProfilesProps> = (props) => {

    const [hideActivities, setHideActivities] = React.useState(false);

    const getMetaData = (userMetricValues: IUserMetricValue[]) => {
        const dailySugarStatMap = { ...props.cgmStats.dailySugarStatMap };
        const rangeBuckets = { ...props.cgmStats.metric.rangeBuckets };
        if (!_isEmpty(rangeBuckets) && !_isEmpty(dailySugarStatMap)) {
            let metaInfo = []
            let totalLength: number = 0;
            let totalSum: number = 0;
            const targetBucket: IRangeBucket = props.cgmStats.metric.rangeBuckets.filter((bucket: IRangeBucket) => bucket.label === "MODERATE")[0];
            const targetGlucoseMinValue: number = targetBucket.rangeMin;
            const targetGlucoseMaxValue: number = targetBucket.rangeMax;
            let inTargetItems: number = 0, belowTargetItems: number = 0, aboveTargetItems: number = 0;


            totalLength = userMetricValues.length;

            for (const userMetricValue of userMetricValues) {
                totalSum += userMetricValue.value;
                if (userMetricValue.value >= targetGlucoseMinValue && userMetricValue.value <= targetGlucoseMaxValue) inTargetItems++;
                else if (userMetricValue.value < targetGlucoseMinValue) belowTargetItems++;
                else if (userMetricValue.value > targetGlucoseMaxValue) aboveTargetItems++;

            }

            const avg = totalSum / totalLength;

            metaInfo.push({
                primaryInfo: `${avg ? avg.toFixed(0) + props.cgmStats.metric.unit : "-"}`,
                secondaryInfo: `Avg Glucose`
            })

            // in target
            const inTargetPerc = (inTargetItems / totalLength) * 100
            metaInfo.push({
                primaryInfo: `${inTargetPerc || inTargetPerc === 0 ? inTargetPerc.toFixed(0) + "%" : "-"}`,
                secondaryInfo: `In Target`
            })

            // below target
            const belowTargetPerc = (belowTargetItems / totalLength) * 100
            metaInfo.push({
                primaryInfo: `${belowTargetPerc || belowTargetPerc === 0 ? belowTargetPerc.toFixed(0) + "%" : "-"}`,
                secondaryInfo: `Below Target`
            })

            // above target
            const aboveTargetPerc = (aboveTargetItems / totalLength) * 100
            metaInfo.push({
                primaryInfo: `${aboveTargetPerc || aboveTargetPerc === 0 ? aboveTargetPerc.toFixed(0) + "%" : "-"}`,
                secondaryInfo: `Above Target`
            })

            return metaInfo;
        }
    }

    const generatePointColorList = (sugarStat: IDailySugarStatValue): string[] => {

        const typeColorMap: { [key: string]: string } = {
            "NUTRITION": "#86cb6b",
            "MEDICATION": "#52abf9",
            "FITNESS": "#f08840",
        }

        const colorsList: string[] = [];

        _forEach(sugarStat.userMetricValues, (metric: IUserMetricValue) => {
            const matchingActivity = sugarStat.patientActivityImpactList.filter((data: IPatientActivityImpactItem) => data.patientActivityLoggingResponse.activityTime === metric.metricDate);
            matchingActivity.length > 0 && matchingActivity[0].patientActivityLoggingResponse.patientActivityType ? colorsList.push(typeColorMap[`${matchingActivity[0].patientActivityLoggingResponse.patientActivityType}`])
                : colorsList.push("transparent");
        })

        return colorsList;
    }

    const getSugarStatsList = () => {
        const list: IDailySugarStatValue[] = [];
        console.log(props.cgmStats);
        const dailySugarStatMap = { ...props.cgmStats.dailySugarStatMap };
        for (const key in dailySugarStatMap) {
            list.push(dailySugarStatMap[key])
        }
        return list;

    }

    const getGraphData = (data: IUserMetricValue[]): { x: string, y: number }[] => {
        data.sort((a, b) => a.metricDate - b.metricDate);
        const r = data.map((dataItem) => ({
            x: moment(dataItem.metricDate).format("YYYY-MM-DD hh:mm:ss A"),
            y: dataItem.value
        }));
        return r;
    }

    const getNormalGlucoseRange = (): { min: number, max: number } => {
        const targetBucket: IRangeBucket = props.cgmStats.metric.rangeBuckets.filter((bucket: IRangeBucket) => bucket.label === "MODERATE")[0];

        return {
            min: targetBucket.rangeMin,
            max: targetBucket.rangeMax
        };
    }

    const fetchActivityDetails = (activityType: string, activity: IPatientActivityLoggingResponse): string => {
        if (activityType === "FITNESS") return `${activity.workoutDetails?.workoutName} ${activity.workoutDetails?.durationInMins ? ' - ' + activity.workoutDetails?.durationInMins + " minutes" : ""}`;
        if (activityType === "NUTRITION") return activity.mealDetails?.notes;
        if (activityType === "MEDICATION") return `${activity.medicineDetails?.quantity} ${activity.medicineDetails.unit} ${activity.medicineDetails?.medicineName}`;
        if (activityType === "SLEEP") return activity.sleepDetails?.notes;
        if (activityType === "MINDFULNESS") return activity.mindDetails?.notes;
        return "";
    }



    const generateActivityTableData = (activityList: IPatientActivityImpactItem[], title?: string): IActivitiesTableProps => {

        const mapActivityTypeToName = (activityType: string): string => {
            if (activityType === "NUTRITION") return "MEAL";
            if (activityType === "FITNESS") return "EXERCISE";
            return activityType;
        }

        const mappedActivityList: IActivity[] = [] as IActivity[];
        const activityTypes: string[] = ["MEAL", "EXERCISE", "MEDICATION"];

        _forEach(activityList, (activity: IPatientActivityImpactItem) => {
            const currAcitivity: IActivity = {} as IActivity;
            currAcitivity.activityTime = moment(activity.patientActivityLoggingResponse.activityTime).format("hh:mm A");
            currAcitivity.activityType = mapActivityTypeToName(activity.patientActivityLoggingResponse.patientActivityType);
            currAcitivity.id = activity.patientActivityLoggingResponse.id;
            currAcitivity.activityDetails = fetchActivityDetails(activity.patientActivityLoggingResponse.patientActivityType, activity.patientActivityLoggingResponse);
            currAcitivity.score = activity.activityScore;
            currAcitivity.intitalReading = activity.currentGlucoseValue;
            currAcitivity.peakReading = activity.glucosePeak;
            currAcitivity.difference = activity.glucoseChange;
            currAcitivity.timeOverTarget = activity.timeOverTarget;
            currAcitivity.isExpandable = activity.glucosePeak
                || activity.timeOverTarget
                || activity.glucoseChange;

            mappedActivityList.push(currAcitivity);

            if (!activityTypes.includes(currAcitivity.activityType)) {
                activityTypes.push(currAcitivity.activityType);
            }

        });

        return { title, activities: mappedActivityList, isMultiType: activityTypes.length > 0, activityTypes, showPagination: true };
    }

    return (
        <div className={"glucose-profiles-container"}>
            <section className={"glucose-profiles-header"}>
                <div>Daily Glucose Profiles</div>
                {/* <div className="glucose-profiles-header--hide" onClick={toggleActivitiesHandler}>Hide Activity Details</div> */}
            </section>
            <section className={"glucose-profile-section"}>
                {
                    _map(getSugarStatsList(), (dataItem: IDailySugarStatValue, index: number) => {
                        return (<div key={index} className={"daily-profile-container"} >
                            {!_isEmpty(dataItem.userMetricValues) && <div className="daily-profile-container__chart">
                                <LineChart
                                    header={moment(dataItem.date).format("DD MMM[']YY [(]dddd[)]")}
                                    legends={[{ text: "Excercise", color: "#86cb6b" }, { text: "Meal", color: "#f08840" }, { text: "Medication", color: "#52abf9" }]}
                                    dayEpoch={dataItem.date}
                                    readings={getGraphData(dataItem.userMetricValues)}
                                    idealRange={getNormalGlucoseRange()}
                                    pointBackgroundColors={generatePointColorList(dataItem)}

                                />
                                <InformationBar infoDetails={getMetaData(dataItem.userMetricValues)} matabolicScore={dataItem.sugarScore.toFixed(0)} />
                            </div>}
                            {!hideActivities ? <div className="daily-profile-container__activities">
                                {/* {dataItem.activities ? <ActivitiesTable activities={getActivities(dataItem.activities)}/>: <div>Activities</div> } */}
                                <ActivitiesTable {...generateActivityTableData(dataItem.patientActivityImpactList)} />
                            </div> : null}
                        </div>)
                    })
                }
            </section>
        </div>
    )
}


const InformationBar = (props: { infoDetails: { primaryInfo: string, secondaryInfo: string }[], matabolicScore: string }) => {

    const getColorFromScore = (score: number): string => {
        if (score >= 100) return "#80c168";
        if (score >= 80) return "#aae692";
        if (score > 40) return "#f6daa3";
        if (score > 20) return "#fea6ac";
        return "#cd4342";
    }

    const formatScore = (score: number): string => {
        score = score / 10;
        if (score === 10) return "10";
        else return score.toFixed(1);
    }

    if (_isEmpty(props.infoDetails)) return null
    return (
        <div className={"information-bar"}>
            <li>
                <p className="metabolic-score" style={{ backgroundColor: getColorFromScore(+props.matabolicScore) }}><span>{formatScore(+props.matabolicScore)}</span></p>
                <p className={"secondary-info-text"}>Metabolic Score</p>
            </li>
            {
                props.infoDetails.map((info: { primaryInfo: string, secondaryInfo: string }, index: number) => (
                    <li>
                        <p className={"primary-info-text"}>{info.primaryInfo}</p>
                        <p className={"secondary-info-text"}>{info.secondaryInfo}</p>
                    </li>
                ))
            }
        </div>
    )
}

export default GlucoseProfiles;